import React, { useState } from 'react';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline password-change section embedded on ProfilePage.
 * Uses the existing session cookie (no need to re-login).
 */
export default function ChangePasswordSection() {
  const { language } = useLanguage();
  const isLV = language === 'lv';

  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setCurrent('');
    setNewPw('');
    setConfirm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (newPw.length < 8) {
      setError(isLV ? 'Parolei jābūt vismaz 8 simbolus garai' : 'Password must be at least 8 characters');
      return;
    }
    if (newPw !== confirm) {
      setError(isLV ? 'Jaunās paroles nesakrīt' : 'New passwords do not match');
      return;
    }
    if (newPw === current) {
      setError(isLV ? 'Jaunā parole nedrīkst būt tāda pati kā pašreizējā' : 'New password must differ from current');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: newPw,
        confirm_password: confirm,
      });
      setSuccess(true);
      reset();
      setTimeout(() => setSuccess(false), 3500);
    } catch (err) {
      setError(formatApiError(err, (isLV ? 'Kļūda. Mēģini vēlreiz.' : 'Error. Try again.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5E4E0] rounded-2xl p-6"
      data-testid="change-password-section"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#D97736]/10 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-[#D97736]" />
        </div>
        <div>
          <h3 className="font-outfit font-semibold text-[#121212]">
            {isLV ? 'Paroles maiņa' : 'Change password'}
          </h3>
          <p className="text-xs text-[#5C5C5C]">
            {isLV ? 'Regulāri maini paroli drošībai' : 'Change your password regularly for security'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="change-pw-error">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm" data-testid="change-pw-success">
            {isLV ? 'Parole veiksmīgi nomainīta' : 'Password changed successfully'}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="current_pw" className="text-sm text-[#121212]">
            {isLV ? 'Pašreizējā parole' : 'Current password'}
          </Label>
          <div className="relative">
            <Input
              id="current_pw"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="h-11 bg-[#FAFAFA] border-[#E5E4E0] pr-10"
              data-testid="current-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#121212]"
              aria-label={show ? 'Hide' : 'Show'}
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="new_pw" className="text-sm text-[#121212]">
              {isLV ? 'Jaunā parole' : 'New password'}
            </Label>
            <Input
              id="new_pw"
              type={show ? 'text' : 'password'}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_pw" className="text-sm text-[#121212]">
              {isLV ? 'Apstiprini' : 'Confirm'}
            </Label>
            <Input
              id="confirm_pw"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
              data-testid="confirm-password-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading || !current || !newPw || !confirm}
          className="w-full h-11 bg-[#D97736] hover:bg-[#C06528] text-white"
          data-testid="change-password-submit-btn"
        >
          {loading
            ? (isLV ? 'Maina...' : 'Changing...')
            : (isLV ? 'Nomainīt paroli' : 'Change password')}
        </Button>
      </form>
    </div>
  );
}
